import { useEffect, useRef, useState, type FormEvent } from 'react'
import {
  catalog,
  F,
  MAX_MEMBER_NUMBER_LENGTH,
  MAX_NAME_LENGTH,
  todayEt,
  type FieldError,
  type OrderForm,
} from '@hearingaid/shared'
import { Card } from '../components/Card'
import { ErrorText } from '../components/ErrorText'
import { submit } from '../lib/data'
import { clearDraft, keepDraft, readDraft } from '../lib/draft'
import { draftAfter, errorsAfter } from '../lib/submitFlow'
import { errorFor, newForm, pickProduct } from '../lib/orderForm'
import { signInAgain } from '../lib/session'
import { ProductPicker } from './ProductPicker'

/** What the last submit left to say beside the button (field errors are separate). */
type Outcome = null | { saveFailed: true } | { sessionExpired: true } | { rejected: string }

function session(): Storage | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

/** A draft kept across a sign-in redirect wins over a blank form (and keeps its submission key). */
function initialForm(): OrderForm {
  const storage = session()
  return (storage && readDraft(storage)) ?? newForm(todayEt(new Date()))
}

export function NewOrder({ onSubmitted }: { onSubmitted: (orderId: string) => void }) {
  const [form, setForm] = useState<OrderForm>(initialForm)
  const [errors, setErrors] = useState<FieldError[] | null>(null)
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<Outcome>(null)
  const inFlight = useRef(false)
  const mounted = useRef(true)
  const heading = useRef<HTMLHeadingElement>(null)
  const today = todayEt(new Date())

  useEffect(() => {
    mounted.current = true
    // The form state now holds any restored draft; storage must not restore it a second time.
    const storage = session()
    if (storage) clearDraft(storage)
    document.title = 'New hearing aid order'
    heading.current?.focus()
    return () => {
      // Leaving the page drops the in-flight result.
      mounted.current = false
    }
  }, [])

  const set = (patch: Partial<OrderForm>) => setForm((f) => ({ ...f, ...patch }))
  const list = errors ?? []

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (inFlight.current) return
    inFlight.current = true
    setBusy(true)
    setOutcome(null)
    const storage = session()
    // Kept first: a token renewal that has to leave the page mid-submit must not lose the form.
    if (storage) keepDraft(storage, form)
    let keepBusy = false
    try {
      const r = await submit(form)
      // Settled first, mounted or not: leaving mid-submit must not strand the participant draft.
      if (storage && draftAfter(r) === 'clear') clearDraft(storage)
      if (!mounted.current) return
      setErrors(errorsAfter(r))
      if (r.ok) {
        keepBusy = true // stay disabled while navigating away
        onSubmitted(r.orderId)
        return
      }
      // Same form, same submission key: a retry that reaches a saved order returns it.
      // An expired session keeps the draft: "Sign in" leaves the page and the form comes back after.
      if ('sessionExpired' in r) setOutcome({ sessionExpired: true })
      else if ('rejected' in r) setOutcome({ rejected: r.message })
      else if ('saveFailed' in r) setOutcome({ saveFailed: true })
    } finally {
      if (!keepBusy) {
        inFlight.current = false
        if (mounted.current) setBusy(false)
      }
    }
  }

  return (
    <>
      <h1 ref={heading} tabIndex={-1}>
        New hearing aid order
      </h1>

      <form onSubmit={onSubmit}>
        <Card icon="person" title="Participant">
          <div className="field-grid">
            <div className="field">
              <label htmlFor="order-date">Order date</label>
              <input
                id="order-date"
                type="date"
                max={today}
                value={form.order_date ?? ''}
                onChange={(e) => set({ order_date: e.target.value || null })}
              />
              <ErrorText field={F.orderDate} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="participant-name">Participant name</label>
              <input
                id="participant-name"
                maxLength={MAX_NAME_LENGTH}
                value={form.participant_name}
                onChange={(e) => set({ participant_name: e.target.value })}
              />
              <ErrorText field={F.participantName} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="member-number">Member number</label>
              <input
                id="member-number"
                maxLength={MAX_MEMBER_NUMBER_LENGTH}
                value={form.member_number}
                onChange={(e) => set({ member_number: e.target.value })}
              />
              <ErrorText field={F.memberNumber} errors={list} />
            </div>
            <div className="field">
              <label htmlFor="center">Senior LIFE center</label>
              <select id="center" value={form.center_id ?? ''} onChange={(e) => set({ center_id: e.target.value || null })}>
                <option value="">Choose…</option>
                {catalog.centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <ErrorText field={F.center} errors={list} />
            </div>
          </div>
        </Card>

        <Card icon="hearing" title="Hearing aid">
          <p className="hint">Pick one. Click it again to clear it.</p>
          <ProductPicker
            products={catalog.products}
            selected={form.product_id}
            onPick={(id, on) => setForm((f) => pickProduct(f, id, on))}
            error={errorFor(list, F.product)}
          />
        </Card>

        <div className="actions">
          {errors !== null && errors.length > 0 && (
            <p className="form-error" role="alert">
              Fix the highlighted fields, then submit again.
            </p>
          )}
          {outcome !== null && 'saveFailed' in outcome && (
            <p className="form-error" role="alert" data-error="Save">
              Couldn't save the order — nothing was sent. Try again.
            </p>
          )}
          {outcome !== null && 'rejected' in outcome && (
            <p className="form-error" role="alert" data-error="Rejected">
              {outcome.rejected}
            </p>
          )}
          {outcome !== null && 'sessionExpired' in outcome && (
            <>
              <p className="form-error" role="alert" data-error="Session">
                Your session has expired. Sign in again to submit — your form will be kept.
              </p>
              <button type="button" className="btn tonal" data-icon="login" onClick={() => void signInAgain()}>
                Sign in
              </button>
            </>
          )}
          <button type="submit" className="btn filled" data-icon="send" disabled={busy}>
            {busy ? 'Submitting…' : 'Submit order'}
          </button>
        </div>
      </form>
    </>
  )
}
